import type { BinaryData } from '@ctw/shared/api/fhir/binaries';
import type { DocumentModel } from '@ctw/shared/api/fhir/models/document';
import { ContentError } from '@ctw/shared/components/errors/content-error';
import { LoadingSpinner } from '@ctw/shared/components/loading-spinner';
import { tiffToPNG } from '@ctw/shared/content/CCDA/tiff-to-png';
import { CdaParser } from '@ctw/shared/content/document-viewer/_internal/parsers/cda-parser';
import { ReactCdaRenderer } from '@ctw/shared/content/document-viewer/_internal/renderers/react/react-cda-renderer';
import { SupportFormLink } from '@ctw/shared/content/support-form-link';
import {
  DOCUMENT_RENDERER_CONTENT_TEST_ID,
  DOCUMENT_RENDERER_TIFF_IMAGE_TEST_ID,
  DOCUMENT_RENDERER_WEB_IMAGE_TEST_ID,
} from '@ctw/shared/test/ids';
import { tw } from '@ctw/shared/utils/tailwind';
import { Viewer as ReactPdfViewer } from '@react-pdf-viewer/core';
import { useEffect, useMemo, useState } from 'react';

const xmlTypes = ['/xml', '/xhtml+xml', 'application/xml', 'text/xml'];
const tiffTypes = ['/tif', '/tiff'];
const webImageTypes = ['/jpg', '/jpeg', '/png', '/apng', '/avif', '/gif', '/webp', '/svg+xml'];

const isSpecificContentType = (extensions: Array<string>, contentType: string) =>
  extensions.some((extension) => contentType.includes(extension));

interface DocumentRendererProps {
  document: DocumentModel;
  binaryData: BinaryData;
}

export const DocumentRenderer = ({ document, binaryData }: DocumentRendererProps) => {
  const cdaParser = useMemo(() => {
    if (binaryData.binary.data && isSpecificContentType(xmlTypes, binaryData.binary.contentType)) {
      return new CdaParser(binaryData.binary.data);
    }

    return null;
  }, [binaryData.binary.contentType, binaryData.binary.data]);

  const [arrayBuffer, setArrayBuffer] = useState<ArrayBuffer>();
  useEffect(() => {
    if (isSpecificContentType(tiffTypes, binaryData.binary.contentType)) {
      // Convert blob to an array buffer, which is used only for tiff files atm.
      void (async () => {
        setArrayBuffer(await binaryData.binary.blob.arrayBuffer());
      })();
    }
  }, [binaryData.binary.blob, binaryData.binary.contentType]);

  if (!binaryData.binary) {
    return <></>;
  }

  if (isSpecificContentType(tiffTypes, binaryData.binary.contentType)) {
    if (arrayBuffer === undefined) {
      return <LoadingSpinner message="Loading image..." centered={true} />;
    }

    const { dataUrl, description } = tiffToPNG(arrayBuffer);
    return (
      <div
        className={tw`flex w-full flex-col items-center justify-center gap-2 p-5`}
        data-testid={DOCUMENT_RENDERER_CONTENT_TEST_ID}
      >
        <p className={tw`text-content-subtle text-sm leading-relaxed`}>{description}</p>
        <img
          key={document.id}
          src={dataUrl}
          alt={description}
          data-testid={DOCUMENT_RENDERER_TIFF_IMAGE_TEST_ID}
          className={tw`h-full max-h-[95vh] w-auto`}
        />
      </div>
    );
  }

  if (isSpecificContentType(webImageTypes, binaryData.binary.contentType)) {
    const url = URL.createObjectURL(binaryData.binary.blob);
    return (
      <div
        className={tw`flex w-full flex-col items-center justify-center gap-2 p-5`}
        data-testid={DOCUMENT_RENDERER_CONTENT_TEST_ID}
      >
        <img
          key={document.id}
          src={url}
          alt=""
          aria-hidden="true"
          data-testid={DOCUMENT_RENDERER_WEB_IMAGE_TEST_ID}
          className={tw`h-full max-h-[95vh] w-auto object-contain`}
        />
      </div>
    );
  }

  if (binaryData.binary.contentType.includes('/pdf')) {
    try {
      const url = URL.createObjectURL(binaryData.binary.blob);
      return (
        <div
          data-testid={DOCUMENT_RENDERER_CONTENT_TEST_ID}
          className={tw`pdf-viewer flex w-full flex-col items-center justify-center gap-2 p-5`}
          key={document.id}
        >
          <ReactPdfViewer fileUrl={url} />
        </div>
      );
    } catch (_error) {
      return (
        <ContentError
          key={document.id}
          title="Unable to preview this PDF file in the web browser."
          className={tw`flex h-full max-h-full min-h-full w-full items-center justify-center`}
        >
          <span>If this problem persists </span>
          <SupportFormLink buttonText="contact support" />.
        </ContentError>
      );
    }
  }

  if (cdaParser) {
    try {
      return <ReactCdaRenderer cdaParser={cdaParser} />;
    } catch (_error) {
      return (
        <ContentError
          key={document.id}
          title="Unable to preview this CDA document in the web browser."
          className={tw`flex h-full max-h-full min-h-full w-full items-center justify-center`}
        >
          <span>If this problem persists </span>
          <SupportFormLink buttonText="contact support" />.
        </ContentError>
      );
    }
  }

  return (
    <ContentError
      key={document.id}
      title="Unable to preview this document."
      className={tw`flex h-full max-h-full min-h-full w-full items-center justify-center`}
    >
      <span>If this problem persists </span>
      <SupportFormLink buttonText="contact support" />.
    </ContentError>
  );
};
